import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { login as loginApi, getPublicLogo } from '../lib/api';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    getPublicLogo().then(res => {
      if (res.data?.logo_url) setLogoUrl(res.data.logo_url);
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const res = await loginApi(email, password);
      login(res.data.access_token, res.data.user);
      toast.success('Welcome back!');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel — White with form */}
      <div className="w-full lg:w-1/2 bg-white flex flex-col justify-between px-8 sm:px-16 lg:px-20 py-10">
        {/* Brand */}
        <div>
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Dealership Logo"
              className="max-h-10 w-auto object-contain"
              data-testid="login-logo"
            />
          ) : (
            <h1 className="text-lg font-bold tracking-tight text-black" style={{ fontFamily: "'Inter', sans-serif" }}>
              Dealer Wingman
            </h1>
          )}
        </div>

        {/* Form — vertically centered */}
        <div className="max-w-sm w-full mx-auto lg:mx-0">
          <h2 className="text-3xl sm:text-4xl font-bold text-black mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
            Sign in
          </h2>
          <p className="text-gray-500 text-sm mb-8">
            Access your deal command center.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-black mb-1.5">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                data-testid="login-email"
                className="w-full h-11 px-3.5 border border-gray-300 rounded-md text-sm text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-shadow"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-black mb-1.5">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                data-testid="login-password"
                className="w-full h-11 px-3.5 border border-gray-300 rounded-md text-sm text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-shadow"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full h-11 bg-black text-white text-sm font-semibold rounded-md hover:bg-gray-900 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="text-xs text-gray-400 flex items-center gap-4">
          <span>&copy; Dealer Wingman {new Date().getFullYear()}</span>
        </div>
      </div>

      {/* Right Panel — Image (hidden on mobile) */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <img
          src="/login-bg.jpg"
          alt="Aerial view of a dealership lot"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Overlay gradient for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        {/* Overlay text at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-10">
          <p className="text-white/90 text-lg font-semibold leading-snug max-w-md">
            "Every deal tracked. Every dollar accounted for. Complete CIT visibility from desk to funding."
          </p>
          <p className="text-white/50 text-sm mt-3">Deal Command Center</p>
        </div>
      </div>
    </div>
  );
};
